import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setInvoiceFilterPeriod,
  getSelectedReconciliationDetailsRequest,
  clearSelectedReconciliation,
  getReconciliationInfoRequest,
} from "../../../redux/actions/reconciliation";
import { convertNumberToFrontend } from "../../../helpers/convert";
import Button from "../../../components/Button";
import { Select } from "../../../components/Inputs";
import DataTable from "../../../components/DataTable";
import Loading from "../../../components/Loading";
import moment from "moment";
import { API_EXPORT } from "../../../helpers/api";
import { setNotification } from "../../../helpers/notification";
import fileDownload from "js-file-download";

const Invoicing = ({ t }) => {
  const dispatch = useDispatch();
  const { status } = useSelector((state) => state.reconciliation.info);
  const { organizationId } = useSelector(
    (state) => state.authorization.user.organization
  );
  const filterPeriod = useSelector(
    (state) => state.reconciliation.invoiceFilterPeriod
  );
  const { loadingDetails, details, itemDetails, subTotalsInPage } = useSelector(
    (state) => state.reconciliation.selectedReconciliation
  );

  const [filterPeriodOptions, setFilterPeriodOptions] = useState([]);
  const [detailsConfig, setDetailsConfig] = useState([]);
  const [invoicingDetailsExportLoading, setInvoicingDetailsExportLoading] =
    useState(false);
  const [downloadFilter, setDownloadFilter] = useState({ term: filterPeriod });

  const detailConstants = [
    {
      totalCredit: 0,
      totalDebit: 0,
      type: "ALIS",
      title: "YEK-G " + t("pages.default.reconciliation.table.buy"),
    },
    {
      totalCredit: 0,
      totalDebit: 0,
      type: "SATIS",
      title: "YEK-G " + t("pages.default.reconciliation.table.sell"),
    },
    {
      totalCredit: 0,
      totalDebit: 0,
      type: "PIU",
      title: "PIU",
    },
    {
      totalCredit: 0,
      totalDebit: 0,
      type: "GZ",
      title: +t("pages.default.reconciliation.table.fee"),
    },
    {
      totalCredit: 0,
      totalDebit: 0,
      type: "DK",
      title: "DK",
    },
  ];
  const typeTitles = {
    ALIS: "YEK-G " + t("pages.default.reconciliation.table.buy"),
    SATIS: "YEK-G " + t("pages.default.reconciliation.table.sell"),
    PIU: "PIU",
    GZ: t("pages.default.reconciliation.table.fee"),
    DK: "DK",
  };
  const statuses = {
    INVOICE: "INVOICE",
  };

  const invoicingDetailsColumns = [
    {
      name: t("pages.default.reconciliation.table.itemName"),
      selector: "type",
      sortable: true,
      cell: (row) => typeTitles[row.type],
    },
    {
      name: t("pages.default.reconciliation.table.credit"),
      selector: "totalCredit",
      sortable: true,
      cell: (row) => (
        <div className="km__u--text-green-300">
          {convertNumberToFrontend({ num: row.totalCredit, digit: 2 })}
        </div>
      ),
      style: { textAlign: "right", paddingRight: "25px" },
      headerStyle: { textAlign: "right", paddingRight: "21px" },
    },
    {
      name: t("pages.default.invoicing.table.creditKDV"),
      selector: "totalCreditKDV",
      sortable: true,
      cell: (row) => (
        <div className="km__u--text-green-300">
          {convertNumberToFrontend({
            num: handleRoundNumber(row.totalCredit * 1.2 - row.totalCredit),
            digit: 2,
          })}
        </div>
      ),
      style: { textAlign: "right", paddingRight: "25px" },
      headerStyle: { textAlign: "right", paddingRight: "21px" },
    },
    {
      name: t("pages.default.reconciliation.table.total"),
      selector: "sumCredit",
      sortable: true,
      cell: (row) => (
        <div className="km__u--text-green-300">
          {convertNumberToFrontend({
            num: handleRoundNumber(row.totalCredit * 1.2),
            digit: 2,
          })}
        </div>
      ),
      style: { textAlign: "right", paddingRight: "25px" },
      headerStyle: { textAlign: "right", paddingRight: "21px" },
    },
    {
      name: t("pages.default.reconciliation.table.debit"),
      selector: "totalDebit",
      sortable: true,
      cell: (row) => (
        <div className="km__u--text-red-300">
          {convertNumberToFrontend({ num: row.totalDebit, digit: 2 })}
        </div>
      ),
      style: { textAlign: "right", paddingRight: "25px" },
      headerStyle: { textAlign: "right", paddingRight: "21px" },
    },
    {
      name: t("pages.default.invoicing.table.debitKDV"),
      selector: "totalDebitKDV",
      sortable: true,
      cell: (row) => (
        <div className="km__u--text-red-300">
          {convertNumberToFrontend({
            num: handleRoundNumber(row.totalDebit * 1.2 - row.totalDebit),
            digit: 2,
          })}
        </div>
      ),
      style: { textAlign: "right", paddingRight: "25px" },
      headerStyle: { textAlign: "right", paddingRight: "21px" },
    },
    {
      name: t("pages.default.reconciliation.table.total"),
      selector: "sumDebit",
      sortable: true,
      cell: (row) => (
        <div className="km__u--text-red-300">
          {convertNumberToFrontend({
            num: handleRoundNumber(row.totalDebit * 1.2),
            digit: 2,
          })}
        </div>
      ),
      style: { textAlign: "right", paddingRight: "25px" },
      headerStyle: { textAlign: "right", paddingRight: "21px" },
    },
  ];

  useEffect(() => {
    dispatch(getReconciliationInfoRequest(filterPeriod));
    dispatch(
      getSelectedReconciliationDetailsRequest({
        organizationId,
        term: filterPeriod,
        operation: "INVOICE",
      })
    );
    return () => {
      dispatch(clearSelectedReconciliation());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    handleCreateInvoicingDetailsTable();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [details, subTotalsInPage]);

  useEffect(() => {
    // set Period options
    let d = new Date();
    let filterPeriods = [];
    for (let i = 1; i <= 11; i++) {
      const name =
        t(`ui.default.months.${moment().subtract(i, "month").format("M")}`) +
        " " +
        moment().subtract(i, "month").format("YYYY");
      filterPeriods.push({
        name,
        value: i === 0 ? "" : moment().subtract(i, "month").format("YYYYMM"),
      });
      d.setMonth(d.getMonth() - 1);
    }
    setFilterPeriodOptions(filterPeriods);
  }, [t, dispatch]);

  const handlePeriodChange = (e) => {
    const { value } = e.target;
    dispatch(setInvoiceFilterPeriod(value));
  };

  const filterReconciliationList = () => {
    dispatch(getReconciliationInfoRequest(filterPeriod));
    dispatch(
      getSelectedReconciliationDetailsRequest({
        organizationId,
        term: filterPeriod,
        operation: "INVOICE",
      })
    );
    setDownloadFilter({
      term: filterPeriod,
    });
    itemDetails.length !== 0 && dispatch(clearSelectedReconciliation());
  };

  const handleCreateInvoicingDetailsTable = () => {
    let updatedDetails = [];
    for (let data of detailConstants) {
      if (!(data.type === typeTitles.DK && status !== statuses.INVOICE)) {
        const foundDetail =
          details && details?.find((detail) => data.type === detail.type);
        if (foundDetail) {
          updatedDetails.push({
            ...foundDetail,
          });
        } else {
          updatedDetails.push({
            ...data,
            totalCredit: 0,
            totalDebit: 0,
          });
        }
      }
    }
    setDetailsConfig(updatedDetails);
  };

  const handleRoundNumber = (num) =>
    Math.round((num + Number.EPSILON) * 100) / 100;

  const downloadInvoicingDetails = async () => {
    setInvoicingDetailsExportLoading(true);
    try {
      const res = await API_EXPORT("invoice/export/excel", {
        ...downloadFilter,
        organizationId,
        operation: "INVOICE",
      });
      const [, filename] =
        res?.headers["content-disposition"].split("filename=");
      fileDownload(res.data, filename);
      setInvoicingDetailsExportLoading(false);
      setNotification("success", t("pages.default.invoicing.downloaded"));
    } catch (err) {
      err.response && setNotification("error", err.response.data.message);
      setInvoicingDetailsExportLoading(false);
    }
  };

  return (
    <>
      <h5 className="km__u--text-has-emphasis">
        {t("pages.default.invoicing.title")}
      </h5>

      <div className="km__p--card">
        <div className="km__p--reconciliation-filter-container">
          <div className="km__u--text_title">
            {t("pages.default.invoicing.title")}
          </div>
          <div className="km__p--reconciliation-filters">
            <Select
              options={filterPeriodOptions}
              name="productionPeriod"
              value={filterPeriod}
              onChange={handlePeriodChange}
              id="in-productionPeriod"
            />
            <Button
              label={t("ui.default.filter")}
              onClick={filterReconciliationList}
              id="in-filter"
            />
            <Button
              type="secondary"
              label={t("ui.default.download")}
              suffixIcon="download"
              onClick={downloadInvoicingDetails}
              disabled={invoicingDetailsExportLoading}
              id="in-download"
            />
          </div>
        </div>
        {!loadingDetails && (
          <DataTable
            columns={invoicingDetailsColumns}
            data={detailsConfig}
            rowKey="type"
            subTotals={[
              {
                totalCount: subTotalsInPage
                  ? convertNumberToFrontend({
                      num: subTotalsInPage.totalCredit,
                      digit: 2,
                    })
                  : 0,
                sumIndex: 1,
                class: "sub-total-right",
              },
              {
                totalCount: subTotalsInPage
                  ? convertNumberToFrontend({
                      num: subTotalsInPage.totalCreditWithWat,
                      digit: 2,
                    })
                  : 0,
                sumIndex: 2,
                class: "sub-total-right",
              },
              {
                totalCount: subTotalsInPage
                  ? convertNumberToFrontend({
                      num: subTotalsInPage.totalCreditAll,
                      digit: 2,
                    })
                  : 0,
                sumIndex: 3,
                class: "sub-total-right",
              },
              {
                totalCount: subTotalsInPage
                  ? convertNumberToFrontend({
                      num: subTotalsInPage.totalDebit,
                      digit: 2,
                    })
                  : 0,
                sumIndex: 4,
                class: "sub-total-right",
              },
              {
                totalCount: subTotalsInPage
                  ? convertNumberToFrontend({
                      num: subTotalsInPage.totalDebitWithWat,
                      digit: 2,
                    })
                  : 0,
                sumIndex: 5,
                class: "sub-total-right",
              },
              {
                totalCount: subTotalsInPage
                  ? convertNumberToFrontend({
                      num: subTotalsInPage.totalDebitAll,
                      digit: 2,
                    })
                  : 0,
                sumIndex: 6,
                class: "sub-total-right",
              },
            ]}
          />
        )}
        {loadingDetails && (
          <Loading
            text={t("pages.default.invoicing.invoicingDetailsLoading")}
          />
        )}
      </div>
    </>
  );
};

export default Invoicing;
